import React, { useState, useEffect, useRef, useCallback } from 'react';

interface BarcodeScannerProps {
  onScan: (barcode: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onScan,
  placeholder = 'Scan barcode...',
  autoFocus = true,
}) => {
  const [barcode, setBarcode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isEditableTarget = (el: HTMLElement | null): boolean => {
    if (!el) return false;
    const tag = el.tagName;
    return (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      el.isContentEditable ||
      !!el.closest('input, textarea, select, [contenteditable="true"]')
    );
  };

  // Focus the input
  const focusInput = useCallback((force = false) => {
    if (!inputRef.current) return;

    const activeEl = document.activeElement as HTMLElement | null;
    const userIsTypingElsewhere = !!activeEl && activeEl !== inputRef.current && isEditableTarget(activeEl);

    if (!force && userIsTypingElsewhere) return;
    inputRef.current.focus();
  }, []);

  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus) {
      // Focus immediately
      focusInput(true);
      // Also focus after a short delay (for page transitions)
      const timer = setTimeout(() => focusInput(true), 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, focusInput]);

  // Refocus when window/tab becomes visible again
  useEffect(() => {
    if (!autoFocus) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => focusInput(), 100);
      }
    };

    const handleWindowFocus = () => {
      setTimeout(() => focusInput(), 100);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [autoFocus, focusInput]);

  // Click anywhere on page to refocus (for scanning workflow)
  useEffect(() => {
    if (!autoFocus) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Don't steal focus from lock screens/modals or interactive controls.
      if (
        target.closest('[data-prevent-scanner-focus]') ||
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.closest('button') ||
        target.closest('a') ||
        isEditableTarget(target)
      ) {
        return;
      }

      // Refocus after a tiny delay to not interfere with the click
      setTimeout(() => focusInput(), 50);
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [autoFocus, focusInput]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && barcode.trim()) {
      e.preventDefault();
      onScan(barcode.trim());
      setBarcode(''); // Clear for next scan

      // Re-focus for continuous scanning
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={barcode}
        onChange={(e) => setBarcode(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full px-4 py-3 text-lg border-2 border-brand-blue rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue font-mono"
        autoComplete="off"
      />
      <div className="absolute right-4 top-3.5">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs font-medium text-slate-600">Ready to scan</span>
        </div>
      </div>
    </div>
  );
};
